import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import moment from "moment";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import Subtext from "components/typography/Subtext";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";

import { attachRfqToWorkOrder, attachWorkToPlan, getPlanAttachableWork, getWorkOrderRfqCandidates } from "api/ServicePlans";

/**
 * Attach existing work (2026-10-15). An RFQ - and the quote and project
 * under it - that already exists is tied to its owner by id:
 *  - mode "workOrder": one RFQ at this work order's site -> the work order.
 *  - mode "plan": any of the plan client's RFQs -> the plan (the site joins
 *    the plan; a work-order-fed plan gets a work order for each).
 * Rows state what each RFQ already carries, so the operator can tell the
 * live one from a stale duplicate.
 */
const QUOTE_STATUS: Record<number, string> = { 0: "new", 1: "accepted", 2: "denied", 3: "draft", 4: "expired", 5: "superseded", 6: "price locked" };
const pad = (n: any) => (n != null ? String(n).padStart(5, "0") : "");

export default function AttachExistingWorkModal({
  mode,
  workOrderId,
  planId,
  planName,
  onClose,
  onDone,
}: {
  mode: "workOrder" | "plan";
  workOrderId?: string;
  planId?: string;
  planName?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  // Work lives on ONE plan: the server answers MOVE_FROM_PLAN first, and
  // the operator confirms the move before it happens.
  const [moveFromPlan, setMoveFromPlan] = useState<{ message: string; moves: any[] } | null>(null);

  const load = async (q?: string) => {
    setLoading(true);
    setError(null);
    const res: any =
      mode === "workOrder" ? await getWorkOrderRfqCandidates(workOrderId as string) : await getPlanAttachableWork(planId as string, q);
    setLoading(false);
    if (!Array.isArray(res)) {
      setError(res?.error ?? "Could not load.");
      setRows([]);
      return;
    }
    setRows(res);
    if (mode === "workOrder" && res.length === 1) setPicked([res[0].rfqId]);
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  // Plan mode searches server-side (a client can have hundreds of RFQs).
  useEffect(() => {
    if (mode !== "plan") return;
    const t = setTimeout(() => load(search.trim() || undefined), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const toggle = (id: string) => {
    setMoveFromPlan(null);
    if (mode === "workOrder") return setPicked([id]);
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const pickedSet = useMemo(() => new Set(picked), [picked]);

  const save = async () => {
    if (!picked.length) return;
    const confirmMove = !!moveFromPlan;
    setSaving(true);
    if (mode === "workOrder") {
      const res: any = await attachRfqToWorkOrder(workOrderId as string, picked[0], confirmMove);
      setSaving(false);
      if (res?.moveFromPlan) return setMoveFromPlan(res.moveFromPlan);
      if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
      toast.add("RFQ attached to this work order.", "var(--theme-ui-colors-success)");
    } else {
      const res: any = await attachWorkToPlan(planId as string, picked, confirmMove);
      setSaving(false);
      if (res?.moveFromPlan) return setMoveFromPlan(res.moveFromPlan);
      if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
      const n = res?.attached?.length ?? 0;
      const skipped = res?.skipped?.length ?? 0;
      toast.add(
        `${n} added to the plan${skipped ? `, ${skipped} skipped (${res.skipped[0]?.reason ?? "see the log"})` : ""}.`,
        skipped ? "var(--theme-ui-colors-warning)" : "var(--theme-ui-colors-success)",
      );
    }
    onDone();
  };

  const siteLine = (r: any) => {
    const a = r.address || {};
    const addr = [a.line1, a.city, a.state].filter(Boolean).join(", ");
    return [r.siteName && r.siteName !== a.line1 ? r.siteName : null, addr || null].filter(Boolean).join(" · ");
  };

  return (
    <Modal isOpen={true} toggle={() => !saving && onClose()} size="md" centered closeOnBackdropClick={false}>
      <ModalHeader toggle={() => !saving && onClose()}>
        {mode === "workOrder" ? "Attach an existing RFQ" : `Add existing work${planName ? ` to ${planName}` : ""}`}
      </ModalHeader>
      <ModalBody>
        <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} marginBottom={"12px"} color={mnt.textMuted}>
          {mode === "workOrder"
            ? "RFQs for this client at this site that no work order owns yet. Its quote and project come with it."
            : "This client's RFQs that no work order owns yet. Each one's site joins the plan, and its quote and project come with it. Added work stays out of batches until you put it in one."}
        </Subtext>
        {mode === "plan" && (
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search RFQ #, name, site or address"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #E4E7EB", borderRadius: "6px", fontFamily: "Poppins", fontSize: "13px", marginBottom: "10px" }}
          />
        )}
        {loading ? (
          <Flex sx={{ justifyContent: "center", padding: "24px" }}>
            <Spinner size={24} />
          </Flex>
        ) : error ? (
          <P fontSize={"13px"} color={themevals.colors.danger}>{error}</P>
        ) : rows.length === 0 ? (
          <P fontSize={"13px"} color={themevals.colors.dkgrey} marginTop={0}>
            {mode === "workOrder" ? "No RFQ for this client at this site is waiting to be attached. Generate one instead." : "Nothing found for this client that isn't already owned by a work order."}
          </P>
        ) : (
          <Box sx={{ maxHeight: "380px", overflowY: "auto", paddingRight: "4px" }}>
            {rows.map((r) => {
              const on = pickedSet.has(r.rfqId);
              const chain = [
                r.quote ? `GMQ-${pad(r.quote.sequence)} ${QUOTE_STATUS[Number(r.quote.status)] ?? ""}`.trim() : "no quote",
                r.project ? `project GMP-${r.project.sequence}` : null,
                r.planId ? `on ${r.planName ?? "another plan"}` : null,
              ].filter(Boolean).join(" · ");
              return (
                <Box
                  key={r.rfqId}
                  onClick={() => toggle(r.rfqId)}
                  sx={{
                    display: "flex", gap: "10px", alignItems: "flex-start", padding: "10px 12px", marginBottom: "6px",
                    border: `1px solid ${on ? "#111" : "#E4E7EB"}`, borderRadius: "6px", cursor: "pointer", fontFamily: "Poppins", fontSize: "13px",
                  }}
                >
                  <input type={mode === "workOrder" ? "radio" : "checkbox"} readOnly checked={on} style={{ marginTop: "3px", width: "auto", flex: "0 0 auto" }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontWeight: 600 }}>
                      {`RFQ #${pad(r.sequence)}`}
                      {r.projectName ? <span style={{ fontWeight: 400 }}>{` · ${r.projectName}`}</span> : null}
                    </Box>
                    <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey }}>{siteLine(r)}</Box>
                    <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey }}>
                      {[chain, r.createdAt ? moment(r.createdAt).format("MMM D, YYYY") : null].filter(Boolean).join(" · ")}
                    </Box>
                    <Evidence r={r} mode={mode} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        {moveFromPlan && (
          <Box sx={{ marginTop: "10px", padding: "10px 12px", background: mnt.amberPill, border: `1px solid ${mnt.amberStroke}`, borderRadius: "6px" }}>
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.amber}>
              {moveFromPlan.message} Work belongs to one plan, so it leaves{" "}
              {Array.from(new Set(moveFromPlan.moves.map((m: any) => m.fromPlanName || "the other plan"))).join(", ")}.
            </Subtext>
            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} color={mnt.amber}>
              {moveFromPlan.moves.map((m: any) => `RFQ #${pad(m.sequence)}`).join(", ")}
            </Subtext>
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton outline scheme="secondary" type="button" keepText disabled={saving} onClick={onClose}>Cancel</BasicButton>
        <BasicButton type="button" keepText disabled={!picked.length || saving} $submitting={saving} onClick={save}>
          {moveFromPlan
            ? `Move ${moveFromPlan.moves.length > 1 ? `${moveFromPlan.moves.length} ` : ""}and ${mode === "workOrder" ? "attach" : "add"}`
            : mode === "workOrder" ? "Attach RFQ" : picked.length > 1 ? `Add ${picked.length} to plan` : "Add to plan"}
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Why an RFQ is offered, signal by signal (2026-10-16): the WO number in its
 * name, the site, the timing. Agreeing signals are green; a conflict is
 * amber, so a match on one signal alone is never mistaken for a sure one.
 */
function Evidence({ r, mode }: { r: any; mode: "workOrder" | "plan" }) {
  const e = r?.evidence;
  if (!e) return null;
  const chips: Array<{ text: string; ok: boolean }> = [];
  if (mode === "workOrder") {
    if (e.woNumberMatches === true) chips.push({ text: `WO# ${e.woNumber} in name`, ok: true });
    else if (e.woNumberMatches === false) chips.push({ text: `Name has a different WO# (${e.woNumber})`, ok: false });
    chips.push(e.sameSite ? { text: "Same site", ok: true } : { text: "Different site record", ok: false });
    if (e.afterReceived === true) chips.push({ text: "After the WO arrived", ok: true });
    else if (e.afterReceived === false) chips.push({ text: "Before the WO arrived", ok: false });
  } else {
    if (e.woNumber) chips.push(e.woNumberTaken ? { text: `WO# ${e.woNumber} already on a work order`, ok: false } : { text: `WO# ${e.woNumber} in name`, ok: true });
    chips.push(e.sameSite ? { text: "Site is on this plan", ok: true } : { text: "Site not on this plan yet", ok: false });
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "4px" }}>
      {chips.map((c) => (
        <span
          key={c.text}
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 10,
            background: c.ok ? "#E1F5EE" : "#FFF8E1",
            color: c.ok ? "#0F6E56" : "#7A5A00",
          }}
        >
          {c.text}
        </span>
      ))}
    </Box>
  );
}
